import { unescape } from 'html-escaper';
import striptags from 'striptags';
import { ROLES } from '../constants';
import treeUtils from './treeutils';

const nunjucks = require('nunjucks');

function mergeFieldToLowerCase(text) {
  if (!text) return text;
  if (typeof text !== 'string') return text.toString();

  const replacer = (match) => match.toLowerCase();

  // FIXME: Regex is not nunjucks, this will cause leaks/unexpected behaviour
  // This is also incompatible with PCG cascading risk, it is case-sensitive
  // https://groups.google.com/forum/#!topic/nunjucks/7zHMk5lmyZs
  const nt = text.replace(/{%\s*.+?\s*%}/g, replacer);
  const nt2 = nt.replace(/{{\s*[\w.]+\s*}}/g, replacer);
  return nt2.replace(/{#\s*[\w.]+\s*#}/g, replacer);
}

function isContextKeyGroupLocked(contextItem, clientDetails) {
  function groupLockedByChild(client, ownerId) {
    if (client.children && client.children.length > 0) {
      return (
        client.children.findIndex((child) => {
          return child.id === ownerId;
        }) > -1
      );
    }
    return false;
  }

  if (contextItem.ownerId) {
    if (
      clientDetails &&
      (clientDetails.id === contextItem.ownerId ||
        groupLockedByChild(clientDetails, contextItem.ownerId))
    ) {
      return false;
    }
    return true;
  }
  return false;
}

function getEditableVariableContextKeyRenderValue(
  text,
  contextItems,
  isAdmin,
  isAdminDraftView,
  pageCommitOID,
  clientDetails
) {
  if (!text) return text;
  if (typeof text !== 'string') return text.toString();

  const replacer = (match) => {
    if (match && match.length) {
      const matchedKey = match.replace('{#', '').replace('#}', '').trim();
      const matchedContextItem = contextItems.find(
        (c) => c.key.toLowerCase() === matchedKey.toLowerCase()
      );

      if (matchedContextItem) {
        let itemValue;

        const isGroupLocked = isContextKeyGroupLocked(matchedContextItem, clientDetails);

        if (matchedContextItem.value) {
          if (isAdmin && !isAdminDraftView) {
            if (!pageCommitOID) {
              itemValue =
                '<span class="validContext" style="cursor:pointer;display:inline;"' +
                ` data-key="${matchedKey}">${matchedContextItem.value}</span>`;
            } else {
              itemValue = `<span class="tooltip validContext">${matchedContextItem.value}<span class="tooltip-text">This value can't be edited.</span></span>`;
            }
          } else {
            itemValue = matchedContextItem.value;
          }
        } else if (matchedContextItem.placeholder) {
          if (!pageCommitOID) {
            if (!isAdmin || isAdminDraftView) {
              itemValue = `<span>${matchedContextItem.placeholder}</span>`;
            } else {
              itemValue = `<span class="placeholder" style="cursor:pointer;" data-key="${matchedKey}">${matchedContextItem.placeholder}</span>`;
            }
          } else if (!isAdmin || isAdminDraftView) {
            itemValue = `<span>${matchedContextItem.placeholder}</span>`;
          } else {
            itemValue = `<span class="tooltip placeholder">${matchedContextItem.placeholder}<span class="tooltip-text">This value can't be edited.</span></span>`;
          }
        } else if (!isAdmin || isAdminDraftView) {
          itemValue = `<span>Missing value for context key ${matchedKey}</span>`;
        } else if (!pageCommitOID) {
          itemValue = `<span class="placeholder" style="cursor:pointer;" data-key="${matchedKey}">Missing value for context key ${matchedKey}</span>`;
        } else {
          itemValue = `<span class="tooltip placeholder">Missing value for context key ${matchedKey}<span class="tooltip-text">This value can't be edited.</span></span>`;
        }

        if (isAdmin && !isAdminDraftView && isGroupLocked) {
          if (itemValue && itemValue.indexOf('<span') > -1) {
            itemValue = itemValue.replace(
              '<span',
              '<span title="This value is managed by your group administrator"'
            );
          } else {
            itemValue = `<span title='This value is managed by your group administrator'>${itemValue}</span>`;
          }
        }
        return itemValue;
      }
    }
    return match;
  };

  return text.replace(/{#\s*[\w.]+\s*#}/g, replacer);
}

function upperCaseOnlyFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function examineTitles(navModArr, originalTitle, newTitle) {
  const titleSet = new Set();
  const values = Object.values(navModArr);

  values.forEach(({ lineage }) => {
    lineage.forEach(({ name }) => {
      // Exclude the current title it has
      if (originalTitle.trim() !== name.trim()) {
        titleSet.add(name.trim());
      }
    });
  });

  return !titleSet.has(newTitle.trim());
}

function getUserRoleText(userRole) {
  let result = '';

  switch (userRole) {
    case ROLES.CLIENT_ADMIN:
      result = 'Client Admin';
      break;

    case ROLES.CLIENT_USER:
      result = 'Client User';
      break;

    case ROLES.CS_ADMIN:
      result = 'CompliSpace Admin';
      break;

    case ROLES.GROUP_ADMIN:
      result = 'Group Admin';
      break;

    case ROLES.NOT_AUTHENTICATED:
    default:
      result = 'Unauthenticated User';
      break;
  }

  return result;
}

function checkIfContextKeysExist(content) {
  const validContextKeyCharacters = 'a-zA-Z0-9_$';
  // Matches any character between {{ }} for Merge Field context keys.
  const regexMergeFieldContextKeys = `(\\{{\\s*[${validContextKeyCharacters}]*\\s*}})`;
  const regexMergeFieldContextKeyPattern = new RegExp(regexMergeFieldContextKeys, 'ig');

  // Matches any character between {% if|elif %} for Variable context keys.
  const regexVariableContextKeys = `(\\{%\\s*(if|elif)\\s*[${validContextKeyCharacters}\\s]*\\s*%})`;
  const regexVariableContextKeyPattern = new RegExp(regexVariableContextKeys, 'ig');

  // Matches any character between {# #} for Editable Variable context keys.
  const regexEditableVariableContextKeys = `(\\{#\\s*[${validContextKeyCharacters}]*\\s*#})`;
  const regexEditableVariableContextKeyPattern = new RegExp(regexEditableVariableContextKeys, 'ig');

  const mergeFieldContextKeys = content.match(regexMergeFieldContextKeyPattern);
  const variableContextKeys = content.match(regexVariableContextKeyPattern);
  const editableVariableContextKeys = content.match(regexEditableVariableContextKeyPattern);

  const mergeFieldContextKeysExist = mergeFieldContextKeys && mergeFieldContextKeys.length;
  const variableContextKeysExist = variableContextKeys && variableContextKeys.length;
  const editableVariableContextKeysExist =
    editableVariableContextKeys && editableVariableContextKeys.length;

  return !!(
    mergeFieldContextKeysExist ||
    variableContextKeysExist ||
    editableVariableContextKeysExist
  );
}

function getContextKeySubstitutedTitle(title, contextValues = {}) {
  try {
    if (checkIfContextKeysExist(title) && Object.keys(contextValues).length) {
      const substitutedContextKeyTitle = nunjucks.renderString(
        mergeFieldToLowerCase(title),
        contextValues
      );

      const substitutedTitle = unescape(striptags(substitutedContextKeyTitle));

      return substitutedTitle;
    }
    return title;
  } catch (error) {
    console.error(`Error while rendering page title ${title} with context key values: `, error);
    return title;
  }
}

function removeContextKeyClass(innerHTML) {
  const placeholderReg = /class="[\w+ *]*placeholder[\w+ *]*"/g;
  const validContextReg = /class="[\w+ *]*validContext[\w+ *]*"/g;

  let result = innerHTML.replace(placeholderReg, (matchString) =>
    matchString.replace(/ *placeholder/gi, '')
  );
  result = result.replace(validContextReg, (matchString) =>
    matchString.replace(/ *validContext/gi, '')
  );

  return result;
}

function removeClickableProperty(innerHTML) {
  let result = innerHTML.replace(/ *onclick="event.preventDefault\(\); editItem\('[\w+]*'\)"/g, '');
  result = result.replace(/ *cursor: pointer;/g, '');
  return result;
}

function resetPageIds(page) {
  let previousPageId = -1;

  const resetIds = (modulePage) => {
    previousPageId += 1;
    // eslint-disable-next-line no-param-reassign
    modulePage.id = previousPageId;

    if (modulePage.children.length > 0) {
      modulePage.children.forEach((child) => resetIds(child, modulePage.id));
    }
  };

  resetIds(page);
}

function resetPageParentIds(page, parentId = -1) {
  // eslint-disable-next-line no-param-reassign
  page.parentId = parentId;

  if (page.children.length > 0) {
    page.children.forEach((child) => resetPageParentIds(child, page.id));
  }
}

function resetPageLevels(page) {
  let list = [];

  const resetLevels = (modulePage, level = 0) => {
    // eslint-disable-next-line no-param-reassign
    modulePage.level = level;
    list = treeUtils.treeToList(page);

    if (modulePage.children.length > 0) {
      modulePage.children.forEach((child) => {
        const parentPage = list.find((listPage) => listPage.id === child.parentId);
        resetLevels(child, parentPage.level + 1);
      });
    }
  };

  resetLevels(page);
}

/**
 * Reorganize toc Id, parentId and level after toc has been changed
 * @param { TOC } toc
 * @returns organized toc
 */
function resetTOCPages(toc) {
  resetPageIds(toc);
  resetPageParentIds(toc);
  resetPageLevels(toc);

  return toc;
}

export {
  mergeFieldToLowerCase,
  getEditableVariableContextKeyRenderValue,
  isContextKeyGroupLocked,
  upperCaseOnlyFirstLetter,
  examineTitles,
  getUserRoleText,
  checkIfContextKeysExist,
  getContextKeySubstitutedTitle,
  removeContextKeyClass,
  removeClickableProperty,
  resetPageIds,
  resetPageParentIds,
  resetPageLevels,
  resetTOCPages
};
